import React, { useEffect, useCallback, useRef } from 'react'
import { Box, Skeleton } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { CreateEauRequestDocument } from 'generated/resources/graphql'
import useLeaveData from 'modules/timeoff/components/EditLeaveModal/useLeaveData'
import { handleResult } from 'modules/core/lib/handleMutation'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import EauStatusCard from 'modules/payroll_bundle/pages/IncidenceDetailPage/_LeaveInfo/EauStatusCard'

interface LeaveConfirmationDetails {
  status?: string
  confirmed_start_date?: string | null
  confirmed_end_date?: string | null
  confirmed_diagnose_date?: string | null
  updated_at?: string | null
  error_messages?: Array<{ error: string }> | null
  remote_request_id?: string | null
  is_initial_attest?: boolean
  is_hospitalisation?: boolean
}

interface EauStatusContainerProps {
  incidenceId: number
  leaveId: number
  employeeId: number
  canRequestEau: boolean
  application: string
}

const EauStatusContainer = ({
  incidenceId,
  leaveId,
  employeeId,
  canRequestEau,
  application,
}: EauStatusContainerProps) => {
  const { company } = useCurrentSession()

  const localStorageKey = LOCAL_STORAGE_KEYS.eauRequestedState(incidenceId)
  const [hasRequestedEau, setHasRequestedEau] = useLocalStorage<boolean>(localStorageKey, false)
  // Ref to store polling cleanup function
  const pollingCleanupRef = useRef<(() => void) | null>(null)
  const isMountedRef = useRef(true)
  const prevStatusRef = useRef<string | null>(null)
  const isInitializedRef = useRef(false)

  const {
    leave: _leave,
    loading,
    leaveConfirmationDetails,
    refetch,
  } = useLeaveData({
    leaveId: String(leaveId),
    leaveEmployeeId: String(employeeId),
  })

  const leaveConfirmationDetailsRef = useRef(leaveConfirmationDetails)

  const [createEauRequest, { loading: isRequesting }] = useMutation(CreateEauRequestDocument, {
    extractorFn: (data) =>
      data.bookkeepersManagementMutations.createEauRequestBookkeepersmanagementIncidence,
  })

  useEffect(() => {
    leaveConfirmationDetailsRef.current = leaveConfirmationDetails
  }, [leaveConfirmationDetails])

  const startPolling = useCallback(() => {
    const delays = [8000, 12000, 18000, 25000, 35000, 50000] // 8s, 12s, 18s, 25s, 35s, 50s
    const timeoutIds: NodeJS.Timeout[] = []
    let hasResult = false

    const cancelRemainingTimeouts = () => {
      timeoutIds.forEach((id) => clearTimeout(id))
      timeoutIds.length = 0
    }

    delays.forEach((baseDelay) => {
      const jitter = Math.random() * 0.3 + 0.85 // ±15% jitter
      const delay = baseDelay * jitter

      const timeoutId = setTimeout(async () => {
        if (hasResult || !isMountedRef.current) return // Skip if we already found a result or component unmounted

        try {
          await refetch()

          const currentData: LeaveConfirmationDetails = leaveConfirmationDetailsRef.current || {}
          const currentStatus = currentData.status || 'request'

          if (currentStatus !== 'request' && isMountedRef.current) {
            hasResult = true
            setHasRequestedEau(false)
            cancelRemainingTimeouts()
          }
        } catch (error) {
          // Silent failure - continue polling even if one request fails
        }
      }, delay)

      timeoutIds.push(timeoutId)
    })

    return cancelRemainingTimeouts
  }, [refetch, leaveConfirmationDetailsRef, setHasRequestedEau])

  useEffect(() => {
    const data: LeaveConfirmationDetails = leaveConfirmationDetails || {}
    const backendStatus = data.status || 'request'
    if ((hasRequestedEau ?? false) && backendStatus !== 'request') {
      setHasRequestedEau(false)
    }
  }, [hasRequestedEau, leaveConfirmationDetails, setHasRequestedEau])

  useEffect(() => {
    if (hasRequestedEau ?? false) {
      const timeout = setTimeout(
        () => {
          setHasRequestedEau(false)
          if (pollingCleanupRef.current) {
            pollingCleanupRef.current()
            pollingCleanupRef.current = null
          }
        },
        2 * 60 * 1000
      )

      return () => clearTimeout(timeout)
    } else {
      if (pollingCleanupRef.current) {
        pollingCleanupRef.current()
        pollingCleanupRef.current = null
      }
    }
  }, [hasRequestedEau, setHasRequestedEau])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (pollingCleanupRef.current) {
        pollingCleanupRef.current()
      }
    }
  }, [])

  const trackingPayload = {
    incidence_id: incidenceId,
    leave_id: leaveId,
    company_id: company.id,
    application,
  }

  const eauDetails: LeaveConfirmationDetails = leaveConfirmationDetails || {}
  const backendStatusEarly = eauDetails.status ?? 'request'
  const eauStatusEarly = (hasRequestedEau ?? false) ? 'in_progress' : backendStatusEarly

  // Track statusViewed on mount (fires only when card renders, not on the null early return)
  useEffect(() => {
    if (loading) return
    if (!canRequestEau && eauStatusEarly === 'request') return
    track(trackingEvents.payroll.eau.statusViewed, {
      ...trackingPayload,
      eau_status: eauStatusEarly,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  // Track terminal status transitions
  useEffect(() => {
    if (loading) return
    const terminalStatuses = ['confirmed', 'dates_mismatch', 'failed_request']

    // On first non-loading render, seed the ref with the current status so we
    // don't fire a tracking event for a leave that is already in a terminal state
    // (the user is just viewing it, not transitioning into it).
    if (!isInitializedRef.current) {
      isInitializedRef.current = true
      prevStatusRef.current = eauStatusEarly
      return
    }

    if (!terminalStatuses.includes(eauStatusEarly)) {
      prevStatusRef.current = eauStatusEarly
      return
    }
    if (prevStatusRef.current === eauStatusEarly) return

    prevStatusRef.current = eauStatusEarly

    const payload = { ...trackingPayload, eau_status: eauStatusEarly }

    if (eauStatusEarly === 'confirmed') {
      track(trackingEvents.payroll.eau.confirmed, payload)
    } else if (eauStatusEarly === 'dates_mismatch') {
      track(trackingEvents.payroll.eau.datesMismatch, payload)
    } else if (eauStatusEarly === 'failed_request') {
      track(trackingEvents.payroll.eau.requestFailed, payload)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eauStatusEarly, loading])

  const handleRequestEau = async () => {
    try {
      const rawResult = await createEauRequest({
        variables: {
          incidenceId: String(incidenceId),
          companyId: String(company.id),
        },
      })

      const result = handleResult(rawResult, () => {})

      if (result.success) {
        const isRetry = backendStatusEarly === 'failed_request'
        const clickPayload = { ...trackingPayload, eau_status: backendStatusEarly }
        if (isRetry) {
          track(trackingEvents.payroll.eau.retryClicked, clickPayload)
        } else {
          track(trackingEvents.payroll.eau.requestClicked, clickPayload)
        }
        setHasRequestedEau(true)
        pollingCleanupRef.current = startPolling()
      }
    } catch (requestError) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('payroll_bundle.eau_status.generic_error'),
      })
    }
  }

  if (loading) {
    return <Skeleton height="s120" />
  }

  const toDate = (dateString?: string | null) => (dateString ? new Date(dateString) : undefined)
  const data: LeaveConfirmationDetails = leaveConfirmationDetails || {}

  const confirmedStartDate = toDate(data.confirmed_start_date)
  const confirmedEndDate = toDate(data.confirmed_end_date)
  const updatedAt = toDate(data.updated_at)

  const backendStatus = backendStatusEarly
  const eauStatus = eauStatusEarly

  if (!canRequestEau && eauStatus === 'request') {
    return null
  }

  return (
    <Box gap="s16">
      <EauStatusCard
        status={eauStatus}
        onRequestClick={handleRequestEau}
        disabled={isRequesting || ((hasRequestedEau ?? false) && backendStatus === 'request')}
        updatedAt={updatedAt}
        confirmedStartDate={confirmedStartDate}
        confirmedEndDate={confirmedEndDate}
        retryable={canRequestEau}
      />
    </Box>
  )
}

export default EauStatusContainer
